import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import PhotoUploader from "@/components/PhotoUploader";
import { toast } from "sonner";
import { Aurora } from "@/components/motion/Aurora";

const EMPTY = {
  sku: "", type: "extra", name: "", short_description: "",
  coverage_details: [], price_type: "per_day", price: 0,
  image_url: "", exclusive_group: null, max_qty: 1, stock: null,
  active: true, sort_order: 100, meta: {},
};

const PRICE_LABEL = { per_day: "€/día", per_rental: "€/viaje", per_unit: "€/unidad" };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product being edited (or EMPTY)
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data.products || []);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editing,
        price: Number(editing.price || 0),
        max_qty: Number(editing.max_qty || 1),
        sort_order: Number(editing.sort_order || 100),
        stock: editing.stock === null || editing.stock === "" ? null : Number(editing.stock),
        exclusive_group: editing.exclusive_group || null,
        coverage_details: (editing.coverage_details || []).filter(Boolean),
      };
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Producto creado");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    try {
      const { data } = await api.delete(`/admin/products/${p.id}`);
      toast.success(data.soft_deleted ? "Producto desactivado (tiene ventas históricas)" : "Producto eliminado");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, { ...p, active: !p.active });
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="aurora-page relative isolate min-h-screen bg-ivory pb-12">
      <Aurora variant="subtle" fixed />
      <header className="bg-white border-b border-sand">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-warmgray hover:text-forest-deep" data-testid="products-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
          <Button onClick={() => setEditing({ ...EMPTY })} className="bg-forest-deep hover:bg-forest text-white" data-testid="products-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuevo producto
          </Button>
        </div>
      </header>

      <main className="container-x py-8">
        <span className="text-overline">Catálogo</span>
        <h1 className="font-display text-3xl font-bold tracking-tight text-charcoal mt-1 mb-6">
          Seguros y extras
        </h1>

        <div className="bg-white border border-sand rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-sand text-left text-xs uppercase tracking-wider text-warmgray-light">
                <th className="px-4 py-3">Producto</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Precio</th>
                <th className="px-4 py-3">Grupo</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Activo</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-b border-sand-light hover:bg-ivory/60" data-testid={`product-row-${p.sku}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="h-10 w-10 rounded-lg object-cover border border-sand" />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-sand-light flex items-center justify-center text-lg">🏕️</div>
                      )}
                      <div>
                        <div className="font-medium text-charcoal">{p.name}</div>
                        <div className="text-xs text-warmgray-light">{p.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${p.type === "insurance" ? "bg-forest-deep/10 text-forest-deep" : "bg-terracotta/10 text-terracotta"}`}>
                      {p.type === "insurance" ? "Seguro" : "Extra"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">
                    {Number(p.price).toFixed(2)} <span className="text-xs text-warmgray-light">{PRICE_LABEL[p.price_type]}</span>
                  </td>
                  <td className="px-4 py-3 text-warmgray">{p.exclusive_group || "—"}</td>
                  <td className="px-4 py-3 text-warmgray">{p.stock ?? "∞"}</td>
                  <td className="px-4 py-3">
                    <Checkbox checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`product-active-${p.sku}`} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setEditing({ ...EMPTY, ...p })} data-testid={`product-edit-${p.sku}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-danger" onClick={() => remove(p)} data-testid={`product-delete-${p.sku}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-warmgray-light">Sin productos todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editar producto" : "Nuevo producto"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nombre</Label>
                  <Input value={editing.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="pform-name" />
                </div>
                <div>
                  <Label>SKU (identificador)</Label>
                  <Input value={editing.sku} onChange={(e) => set("sku", e.target.value)} className="mt-1.5" data-testid="pform-sku" />
                </div>
              </div>
              <div>
                <Label>Descripción corta</Label>
                <Input value={editing.short_description} onChange={(e) => set("short_description", e.target.value)} className="mt-1.5" data-testid="pform-desc" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={editing.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="pform-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="extra">Extra</SelectItem>
                      <SelectItem value="insurance">Seguro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Modo de precio</Label>
                  <Select value={editing.price_type} onValueChange={(v) => set("price_type", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="pform-price-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_day">Por día</SelectItem>
                      <SelectItem value="per_rental">Por viaje</SelectItem>
                      <SelectItem value="per_unit">Por unidad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Precio (€)</Label>
                  <Input type="number" min="0" step="0.5" value={editing.price} onChange={(e) => set("price", e.target.value)} className="mt-1.5" data-testid="pform-price" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Grupo excluyente</Label>
                  <Select
                    value={editing.exclusive_group || "none"}
                    onValueChange={(v) => set("exclusive_group", v === "none" ? null : v)}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="pform-group"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Ninguno (acumulable)</SelectItem>
                      <SelectItem value="insurance">insurance (seguros)</SelectItem>
                      <SelectItem value="km">km (kilómetros)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Cantidad máx.</Label>
                  <Input type="number" min="1" value={editing.max_qty} onChange={(e) => set("max_qty", e.target.value)} className="mt-1.5" data-testid="pform-maxqty" />
                </div>
                <div>
                  <Label>Stock (vacío = ∞)</Label>
                  <Input type="number" min="0" value={editing.stock ?? ""} onChange={(e) => set("stock", e.target.value === "" ? null : e.target.value)} className="mt-1.5" data-testid="pform-stock" />
                </div>
              </div>
              {editing.type === "insurance" && (
                <div>
                  <Label>Coberturas (una por línea)</Label>
                  <Textarea
                    value={(editing.coverage_details || []).join("\n")}
                    onChange={(e) => set("coverage_details", e.target.value.split("\n"))}
                    rows={4}
                    className="mt-1.5"
                    placeholder={"Franquicia reducida a 150 €\nDaños en lunas incluidos"}
                    data-testid="pform-coverage"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <Label>Orden</Label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => set("sort_order", e.target.value)} className="mt-1.5" data-testid="pform-order" />
                </div>
                <label className="flex items-center gap-2 pb-2 cursor-pointer">
                  <Checkbox checked={editing.active} onCheckedChange={(v) => set("active", Boolean(v))} data-testid="pform-active" />
                  <span className="text-sm">Visible para clientes</span>
                </label>
              </div>
              <div>
                <PhotoUploader
                  label="Imagen del producto"
                  value={editing.image_url}
                  onChange={(v) => set("image_url", v)}
                  adminUpload
                  testid="pform-image"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="border-sand">Cancelar</Button>
                <Button
                  onClick={save}
                  disabled={saving || !editing.name || !editing.sku}
                  className="bg-forest-deep hover:bg-forest text-white"
                  data-testid="pform-save"
                >
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
